import {
    type QueryClient,
    useMutation,
    useQuery,
    UseQueryOptions,
} from "@tanstack/react-query";
import { keepPreviousDataOnPageChange } from "./keepPreviousDataOnPageChange";
import { useChunkQueries } from "./useChunkQueries";
import { toNarrowedCounts } from "@components/activityTimeline/activityTimelineNarrowedCounts.lib";
import {
    auditLogContract,
    RevertHistoryPreviewResponseSchema,
    RevertHistoryRequestSchema,
} from "@common/contracts/auditLog.contract";
import { useScope } from "@state/scopeContext.lib";
import { ServerInferRequest } from "@ts-rest/core";
import { z } from "zod";
import {
    AuditLogQuery,
    AuditLogResponse,
    HistoryDetailsQuery,
    HistoryDetailsResponse,
    GroupedHistoryDetailsResponse,
    SourceIdGroupedResponse,
    HistoryQuery,
    HistoryResponse,
    HistoryDailyCountsQuery,
    HistoryDailyCountsResponse,
} from "@common/types/auditLog";
import { getBackendAPIClient } from "@lib/backendAPIClient";

type GetAuditLogParams = ServerInferRequest<
    typeof auditLogContract.getAuditLogs
>["query"];

type GetHistoryParams = ServerInferRequest<
    typeof auditLogContract.getHistory
>["query"];

type GetHistoryDailyCountsParams = ServerInferRequest<
    typeof auditLogContract.getHistoryDailyCounts
>["query"];

type GetHistoryDetailsParams = ServerInferRequest<
    typeof auditLogContract.getHistoryDetails
>["query"];

interface UseGetAuditLogsQueryOptions extends GetAuditLogParams {
    options?: Omit<
        UseQueryOptions<AuditLogResponse, Error>,
        "queryKey" | "queryFn"
    >;
}

interface UseGetHistoryQueryOptions extends GetHistoryParams {
    options?: Omit<
        UseQueryOptions<HistoryResponse, Error>,
        "queryKey" | "queryFn"
    >;
}

interface UseGetHistoryDailyCountsQueryOptions extends GetHistoryDailyCountsParams {
    options?: Omit<
        UseQueryOptions<HistoryDailyCountsResponse, Error>,
        "queryKey" | "queryFn"
    >;
}

interface UseGetHistoryDetailsQueryOptions extends GetHistoryDetailsParams {
    options?: Omit<
        UseQueryOptions<
            | HistoryDetailsResponse
            | GroupedHistoryDetailsResponse
            | SourceIdGroupedResponse,
            Error
        >,
        "queryKey" | "queryFn"
    >;
}

const auditLogContractClient = getBackendAPIClient(auditLogContract);

export const auditLogQueryKeys = {
    all: ["audit-log"] as const,
    workspace: (workspaceId: string, query?: AuditLogQuery) => [
        ...auditLogQueryKeys.all,
        "workspace",
        workspaceId,
        query,
    ],
    byResource: (params: {
        workspaceId: string;
        resourceType: string;
        resourceId: string;
        query?: AuditLogQuery;
    }) => [
        ...auditLogQueryKeys.all,
        "byResource",
        params.workspaceId,
        params.resourceType,
        params.resourceId,
        params.query,
    ],
    history: (workspaceId: string, query?: HistoryQuery) => [
        ...auditLogQueryKeys.all,
        "history",
        workspaceId,
        query,
    ],
    historyDailyCounts: (
        workspaceId: string,
        query?: HistoryDailyCountsQuery,
    ) => [...auditLogQueryKeys.all, "history-daily-counts", workspaceId, query],
    historyDetails: (workspaceId: string, query?: HistoryDetailsQuery) => [
        ...auditLogQueryKeys.all,
        "history-details",
        workspaceId,
        query,
    ],
};

/** Refetches one workspace's grouped history and its expanded lines, e.g. after an edit adds a line. */
export const invalidateWorkspaceHistoryQueries = (
    queryClient: QueryClient,
    workspaceId: string,
) =>
    Promise.all([
        queryClient.invalidateQueries({
            queryKey: [...auditLogQueryKeys.all, "history", workspaceId],
        }),
        queryClient.invalidateQueries({
            queryKey: [
                ...auditLogQueryKeys.all,
                "history-details",
                workspaceId,
            ],
        }),
    ]);

function getAuditLogsQueryKey(
    workspaceId: string,
    query: AuditLogQuery,
): readonly unknown[] {
    const { filters } = query;
    if (filters?.resourceType && filters?.resourceId) {
        return auditLogQueryKeys.byResource({
            workspaceId,
            resourceType: filters.resourceType,
            resourceId: filters.resourceId,
            query,
        });
    }
    return auditLogQueryKeys.workspace(workspaceId, query);
}

export function useGetAuditLogsQuery({
    filters,
    page,
    limit,
    sort,
    options,
}: UseGetAuditLogsQueryOptions) {
    const { workspaceId } = useScope();

    const query: AuditLogQuery = {
        filters,
        page,
        limit,
        sort,
    };

    const queryKey = getAuditLogsQueryKey(workspaceId ?? "", query);

    return useQuery({
        queryKey,
        queryFn: async () => {
            if (!workspaceId) {
                throw new Error("Workspace ID is required");
            }

            const response = await auditLogContractClient.getAuditLogs({
                params: {
                    workspaceId,
                },
                query,
            });

            return response.body;
        },
        enabled: !!workspaceId && (options?.enabled ?? true),
        placeholderData: keepPreviousDataOnPageChange(queryKey),
        ...options,
    });
}

export function useGetHistoryQuery({
    filters,
    page,
    limit,
    groupByChannel,
    relatedResource,
    options,
}: UseGetHistoryQueryOptions) {
    const { workspaceId } = useScope();

    const query: HistoryQuery = {
        filters,
        page,
        limit,
        groupByChannel,
        relatedResource,
    };

    const queryKey = auditLogQueryKeys.history(workspaceId ?? "", query);

    return useQuery({
        queryKey,
        queryFn: async () => {
            if (!workspaceId) {
                throw new Error("Workspace ID is required");
            }

            const response = await auditLogContractClient.getHistory({
                params: {
                    workspaceId,
                },
                query,
            });

            return response.body;
        },
        enabled: !!workspaceId && (options?.enabled ?? true),
        placeholderData: keepPreviousDataOnPageChange(queryKey),
        ...options,
    });
}

const historyDailyCountsQueryOptions = ({
    workspaceId,
    query,
}: {
    workspaceId: string | null | undefined;
    query: HistoryDailyCountsQuery;
}) => ({
    queryKey: auditLogQueryKeys.historyDailyCounts(workspaceId ?? "", query),
    queryFn: async () => {
        if (!workspaceId) {
            throw new Error("Workspace ID is required");
        }

        const response = await auditLogContractClient.getHistoryDailyCounts({
            params: {
                workspaceId,
            },
            query,
        });

        return response.body;
    },
});

export function useGetHistoryDailyCountsQuery({
    filters,
    bucket,
    timeZone,
    breakdown,
    options,
}: UseGetHistoryDailyCountsQueryOptions) {
    const { workspaceId } = useScope();

    return useQuery({
        ...historyDailyCountsQueryOptions({
            workspaceId,
            query: { filters, bucket, timeZone, breakdown },
        }),
        enabled: !!workspaceId && (options?.enabled ?? true),
        ...options,
    });
}

/** A counts chunk by its start, which stays put while its end moves with today. */
export const toCountsChunkKey = ({
    filters,
}: {
    filters?: { minCreatedAt?: Date };
}) => filters?.minCreatedAt?.toISOString() ?? "";

const PAST_CHUNK_CACHE_MS = 10 * 60 * 1000;

/** A counts chunk that ended before now barely changes, so it stays fresh longer than the one with today. */
export const toCountsChunkCacheOptions = ({
    filters,
}: {
    filters?: { maxCreatedAt?: Date };
}) =>
    filters?.maxCreatedAt && filters.maxCreatedAt.getTime() < Date.now()
        ? { staleTime: PAST_CHUNK_CACHE_MS, gcTime: PAST_CHUNK_CACHE_MS }
        : {};

/** One workspace counts query per chunk, see `useChunkQueries`. */
export function useGetHistoryDailyCountsQueries({
    queries,
}: {
    queries: readonly HistoryDailyCountsQuery[];
}) {
    const { workspaceId } = useScope();

    return useChunkQueries({
        scopeId: workspaceId ?? "",
        queries,
        toChunkKey: toCountsChunkKey,
        toQueryOptions: (query) => ({
            ...historyDailyCountsQueryOptions({ workspaceId, query }),
            ...toCountsChunkCacheOptions(query),
        }),
        toPlaceholder: toNarrowedCounts,
    });
}

export function useGetHistoryDetailsQuery({
    date,
    sourceType,
    resourceType,
    changeTypes,
    actorId,
    organizationActorId,
    entityIds,
    fields,
    sourceIds,
    search,
    page,
    limit,
    grouped,
    drillDownResourceType,
    drillDownChangeType,
    showReverted,
    groupBySourceId,
    channel,
    channels,
    outcome,
    relatedResource,
    options,
}: UseGetHistoryDetailsQueryOptions) {
    const { workspaceId } = useScope();

    const query: HistoryDetailsQuery = {
        date,
        sourceType,
        resourceType,
        changeTypes,
        actorId,
        organizationActorId,
        entityIds,
        fields,
        sourceIds,
        search,
        page,
        limit,
        grouped,
        drillDownResourceType,
        drillDownChangeType,
        showReverted,
        groupBySourceId,
        channel,
        channels,
        outcome,
        relatedResource,
    };

    const queryKey = auditLogQueryKeys.historyDetails(workspaceId ?? "", query);

    return useQuery({
        queryKey,
        queryFn: async () => {
            if (!workspaceId) {
                throw new Error("Workspace ID is required");
            }

            const response = await auditLogContractClient.getHistoryDetails({
                params: {
                    workspaceId,
                },
                query,
            });

            return response.body;
        },
        enabled: !!workspaceId && (options?.enabled ?? true),
        placeholderData: keepPreviousDataOnPageChange(queryKey),
        ...options,
    });
}

type RevertHistoryBody = z.infer<typeof RevertHistoryRequestSchema>;
type RevertHistoryPreview = z.infer<typeof RevertHistoryPreviewResponseSchema>;
export type RevertHistoryPreviewBody = Omit<RevertHistoryBody, "dryRun">;

export function useRevertHistoryMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (body: RevertHistoryBody) => {
            if (!workspaceId) {
                throw new Error("Workspace ID is required");
            }

            const response = await auditLogContractClient.revertHistory({
                params: { workspaceId },
                body,
            });

            if (response.status === 200) {
                return response.body;
            }

            throw new Error("Failed to revert history");
        },
    });
}

function isRevertHistoryPreview(body: unknown): body is RevertHistoryPreview {
    return (
        typeof body === "object" &&
        body !== null &&
        "fields" in body &&
        "transactionCount" in body
    );
}

interface UseRevertHistoryPreviewQueryOptions {
    body: RevertHistoryPreviewBody | null;
    enabled?: boolean;
}

const revertHistoryPreviewQueryKey = (
    workspaceId: string,
    body: RevertHistoryPreviewBody | null,
) => ["audit-log", "revert-preview", workspaceId, body] as const;

export function useRevertHistoryPreviewQuery({
    body,
    enabled = true,
}: UseRevertHistoryPreviewQueryOptions) {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: revertHistoryPreviewQueryKey(workspaceId ?? "", body),
        queryFn: async (): Promise<RevertHistoryPreview> => {
            if (!workspaceId) {
                throw new Error("Workspace ID is required");
            }
            if (!body) {
                throw new Error("Preview body is required");
            }

            const response = await auditLogContractClient.revertHistory({
                params: { workspaceId },
                body: { ...body, dryRun: true },
            });

            if (response.status !== 200) {
                throw new Error("Failed to load revert preview");
            }
            if (!isRevertHistoryPreview(response.body)) {
                throw new Error("Unexpected revert preview response shape");
            }
            return response.body;
        },
        enabled: !!workspaceId && !!body && enabled,
    });
}
